import { useQueryClient } from '@tanstack/react-query';

import {
  getApiV1CourseregistrationDocumentCourseregistrationidAttachmentsQueryOptions,
  usePostApiV1CourseregistrationDocumentStatus,
} from '@/shared/api/generated';

/**
 * Hook para atualizar o status de um documento de matrícula (aba Documentação).
 *
 * Encapsula `POST /api/v1/courseregistration/document/status` e invalida
 * a query de attachments da matrícula após cada operação bem-sucedida.
 *
 * EDocumentationStatus: 1 = Approved | 2 = Rejected | 3 = ApprovedTemporary
 *
 * @param enrollmentId - ID da matrícula
 * @param personId - ID da pessoa dona dos documentos
 * @param onSuccess - Callback chamado após cada atualização bem-sucedida
 * @returns Ações de status e estado de envio
 *
 * @example
 * ```tsx
 * const docStatus = useEnrollmentDocumentStatus(enrollmentId, personId, () => setModal(null));
 * docStatus.approveDocument(enrollmentDocumentId);
 * ```
 */
export function useEnrollmentDocumentStatus(
  enrollmentId: number,
  personId: number,
  onSuccess?: () => void,
) {
  const queryClient = useQueryClient();

  const mutation = usePostApiV1CourseregistrationDocumentStatus({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries(
          getApiV1CourseregistrationDocumentCourseregistrationidAttachmentsQueryOptions(
            enrollmentId,
          ),
        );
        onSuccess?.();
      },
    },
  });

  return {
    approveDocument: (enrollmentDocumentId: number) =>
      mutation.mutate({
        data: { courseRegistrationId: enrollmentId, personId, status: 1, enrollmentDocumentId },
      }),

    approveDocumentTemporary: (enrollmentDocumentId: number) =>
      mutation.mutate({
        data: { courseRegistrationId: enrollmentId, personId, status: 3, enrollmentDocumentId },
      }),

    rejectDocument: (enrollmentDocumentId: number, observationId: number) =>
      mutation.mutate({
        data: {
          courseRegistrationId: enrollmentId,
          personId,
          status: 2,
          enrollmentDocumentId,
          observationId,
        },
      }),

    isPending: mutation.isPending,
    isError: mutation.isError,
  } as const;
}
